import { HISTORY_SEASONS, historyLabel, historyFoundingSeason } from "game/time";
import { personSize, LIFE_STAGES } from "game/life_stages";
import { installStoredHistory } from "game/world_history";
import { treeAllowed, reserveBuildingClearance } from "game/tree_clearance";
import { landFieldFor, mainLandKeys, buildGeography, groundForState, safeTile, naturalGroundFor, settlementSite, suitableHouse } from "game/geography";
import { prepareEntrances, worldKey, tileAt } from "game/travel_geometry";
import { generateHistory, giveTradeTools } from "game/history";
import { reserveFoundingFields } from "game/settlement";
import { FULL_TRACK } from "game/road_wear";
import { markThroughRoutes } from "game/settlement_routes";
import SimplexNoise from "simplex-noise";
import * as math from "game/math";
import { HEX_SIZE, Hex, Grid, ALL_SIDES } from "game/hex";

import MersenneTwister from "mersenne-twister";


import { newEntities } from "game/entities";
import { discoverTiles } from "game/playable";
import { addBidirectionalLink, removeBidirectionalLink } from "game/pathfinding";

import { HIGH_GROUND } from "game/height";
import { localHabitat, woodsAt } from "game/biome_field";
import { generateWorld, homeOf, tileTemperature, biomeOf } from "game/overworld";
import { initializeEcology, generateForage } from "game/ecology";
import { generateVillageLife } from "game/villages";
import { expansionFits, expandedFootprint } from "game/settlement_growth";
import { updateWeather } from "game/weather";
import { generatePersonality, initializeRelationships } from "game/people";
import { orientationForSeed } from "game/orientation";
import { MAP_RADIUS, MAP_CENTRE, worldPoint } from "game/world_terrain";

export { HEX_SIZE, Hex, InnerHex, Grid, ALL_SIDES } from "game/hex";

const MIN_START_SETTLEMENT_DISTANCE = 3;
const STARTING_KNOWN_DISTANCE = 5;

const UPDATE_PROGRESS_EVERY = 500;

export const HAIR = [
  0x8D3633, // red
  0x292021, // brown
  0x20232A, // black
  0x957E3C, // blonde
  // TODO: Should only be for old peopl
  // 0xd6cabf, // blonde grey
  0x997f76, // strawberry blonde
  0xe27bc8, // pink
  0x7a95e2, // blue
  // TODO: Should only be for old peopl
  // 0xbfcbd6, // grey
];

export const BODY_MALE = 0x4E3F30;
export const BODY_FEMALE = 0x3B6071;

export function generatePerson({ spatial, male, age, familySeed, generator, days }) {
  const seed = generator.random_int();
  return {
    nameable: { type: "person", familySeed, male, seed },
    spatial,
    kinship: { parents: [], spouse: null, children: [] },
    personality: { ...generatePersonality(generator), orientation: orientationForSeed(seed) },
    traits: { values: {} },
    attributes: {},
    controllable: {},
    holder: { held: [], capacity: 2 },
    personable: {
      type: "person",
      age,
      size: personSize(age),
      male,
      hair: generator.random_int() % HAIR.length,
      body: male ? BODY_MALE : BODY_FEMALE,
      tiredness: 0.2 + generator.random_incl() * 0.4,
      hunger: 0.4 + generator.random_incl() * 0.4
    },
    manager: { jobs: [] },
    planner: { world: { no_place_for: {}, jobs: [], places: {}, feeling: {}, holding: {}, days } }
  };
}

export function generateFamilies({ state, seed, playerStartTile, founding = false }) {
  const generator = new MersenneTwister(seed);
  const homes = {};
  for (const buildingId in state.ecs.building) {
    const spatial = Hex().fromPoint(state.ecs.spatial[buildingId]);
    const villageId = spatial.x == playerStartTile.x && spatial.y == playerStartTile.y ?
      null : state.ecs.village[buildingId].villageId;
    const home = { buildingId, spatial, villageId, people: [] };
    (homes[villageId] ||= []).push(home);
  }

  const inhabit = (home, age, male, familySeed, parents = []) => {
    // These children were born before generation, when their parents were younger.
    if (parents.length) {
      const youngest = Math.max(...parents.map(id => state.ecs.personable[id].age -
        (state.ecs.nameable[id].male ? LIFE_STAGES.old.from : LIFE_STAGES.middleAged.from) + 1));
      const oldest = Math.min(...parents.map(id => state.ecs.personable[id].age - LIFE_STAGES.youngAdult.from));
      age = Math.max(youngest, Math.min(oldest, age));
    }
    const entrance = state.ecs.building[home.buildingId].entrance;
    const sides = Array.from({ length: 6 }, (v, i) => (entrance + i) % 6);
    const starts = Grid.hexagon({ center: home.spatial, radius: 1 }).neighborsOf(home.spatial, sides);
    const [id] = newEntities(state, [{
      ...generatePerson({ spatial: starts[home.people.length].toPoint(), male, age,
        familySeed, generator, days: state.days }),
      village: { villageId: home.villageId },
      household: { houseId: home.buildingId }
    }], "person-");
    home.people.push(id);
    state.ecs.kinship[id].parents = [...parents];
    for (const parent of parents) state.ecs.kinship[parent].children.push(id);
    state.ecs.controllable[id].controllerId = home.people[0];
    state.ecs.controllable[home.buildingId].controllerId = home.people[0];
    return id;
  };
  const marry = (a, b) => {
    state.ecs.kinship[a].spouse = b;
    state.ecs.kinship[b].spouse = a;
  };

  for (const villageId in homes) {
    const houses = homes[villageId];
    if (villageId == "null") {
      for (const home of houses) {
        const id = inhabit(home, 20 + generator.random_int() % 16, generator.random_int() % 2 == 0, generator.random_int());
        state.ecs.playable ||= {};
        state.ecs.playable[id] = { id, known: [] };
      }
      continue;
    }
    // Three living generations: elders, their adult children in neighbouring
    // houses, and grandchildren. A few grown grandchildren live on their own.
    const familySeed = generator.random_int();
    const elders = [inhabit(houses[0], 66 + generator.random_int() % 7, true, familySeed)];
    if (generator.random_int() % 3 != 0) {
      elders.push(inhabit(houses[0], 64 + generator.random_int() % 7, false, familySeed));
      marry(...elders);
    }
    let firstParents;
    for (let h = 1; h < houses.length; h++) {
      const home = houses[h];
      const male = generator.random_int() % 2 == 0;
      if (!founding && h > 2 && generator.random_int() % 4 == 0) {
        inhabit(home, 18 + generator.random_int() % 5, male, familySeed, firstParents);
        continue;
      }
      const adultAge = founding ? 22 + generator.random_int() % 25 : 40 + generator.random_int() % 5;
      const head = inhabit(home, adultAge, male, familySeed, !founding || h == 1 ? elders : []);
      const spouse = inhabit(home, adultAge, !male, generator.random_int());
      marry(head, spouse);
      const parents = [head, spouse];
      if (h == 1) firstParents = parents;
      const children = founding ? 0 : 1 + generator.random_int() % 2;
      for (let c = 0; c < children; c++) {
        inhabit(home, 2 + generator.random_int() % 15, generator.random_int() % 2 == 0, familySeed, parents);
      }
    }
  }
  if (!state.ecs.playable || !Object.keys(state.ecs.playable).length) {
    const [id] = newEntities(state, [{
      ...generatePerson({ spatial: Hex(playerStartTile).toPoint(), male: generator.random_int() % 2 == 0,
        age: 20 + generator.random_int() % 16, familySeed: generator.random_int(), generator, days: state.days }),
      village: { villageId: null }
    }], "person-");
    state.ecs.playable = { [id]: { id, known: [] } };
    state.ecs.manager[id].ordered = true;
    state.ecs.planner[id].world.ordered = true;
    state.ecs.controllable[id].controllerId = id;
  }
  initializeRelationships(state, seed);
}

export async function generateTerrain(grid, world, progressUpdate) {
  const field = landFieldFor(world);
  const home = homeOf(world);
  const result = {};
  for (let i = 0; i < grid.length; i++) {
    const hex = grid[i];
    const point = hex.toPoint();
    result[hex] = { ...point, terrain: field.terrainAt(worldPoint(home, point)), elevation: field.elevationAt(worldPoint(home, point)) };
    if (i % UPDATE_PROGRESS_EVERY == 0) { await progressUpdate("Shaping the land", .12 * (i + 1) / grid.length); }
  }
  return result;
}

const GRID_STEP = 30;

// NOTE: How thickly each biome grows trees. Trees stand on grassland
// wherever the woods noise is above the cutoff, and then only at the share
// of those spots given by keep, so a low cutoff with a small keep scatters
// lone trees widely while a high one keeps them to a few patches. Grassland
// keeps the cutoff it always had.
const TREE_DENSITY = {
  jungle: { cutoff: 0, keep: 1 },
  taiga: { cutoff: 0.3, keep: 1 },
  grassland: { cutoff: 0.6, keep: 1 },
  marsh: { cutoff: 0.2, keep: 0.12 },
  savanna: { cutoff: -0.2, keep: 0.035 },
  steppe: { cutoff: 0, keep: 0.04 },
  snowfield: { cutoff: 0.2, keep: 0.06 },
  tundra: { cutoff: 0.4, keep: 0.025 },
  desert: { cutoff: 0.4, keep: 0.02 },
};

// Blend actual establishment probabilities, including a narrow soft forest
// edge. Named fixtures retain their original exact density convention.
export function treeProbability(woods, habitat) {
  if(typeof habitat == "string") {
    const {cutoff,keep}=TREE_DENSITY[habitat];
    return woods>cutoff?keep:0;
  }
  let probability=0;
  for(const biome in habitat) {
    const {cutoff,keep}=TREE_DENSITY[biome];
    probability+=habitat[biome]*keep*Math.max(0,Math.min(1,(woods-cutoff+.1)/.2));
  }
  return probability;
}

// NOTE: Whatever the biome, a few trees always grow on land the start can
// walk to within this many hexes, so there is wood to be had.
export const MIN_START_TREES = 6;
const START_TREE_REACH = 4;

// NOTE: a number from 0 to 1 that is always the same for the same text
function hashUnit(text) {
  let h = 2166136261;
  for (let i = 0; i < text.length; i++) {
    h = Math.imul(h ^ text.charCodeAt(i), 16777619);
  }
  h = Math.imul(h ^ (h >>> 16), 0x85EBCA6B);
  h = Math.imul(h ^ (h >>> 13), 0xC2B2AE35);
  return ((h ^ (h >>> 16)) >>> 0) / 4294967296;
}

// NOTE: the land hexes the start can walk to, not through other houses
function reachableFrom(start, landscape, settlements, reach) {
  const reached = new Set([start.toString()]);
  let frontier = [start];
  for (let d = 0; d < reach; d++) {
    frontier = frontier.flatMap(hex => Grid.hexagon({ center: hex, radius: 1 }).neighborsOf(hex, ALL_SIDES)
      .filter((n, side) => hex.toString() != start.toString() || !settlements[start] ||
        side == settlements[start].frontDoorSide))
      .filter(hex => {
        const key = hex.toString();
        if (reached.has(key) || !landscape[key] || !walkable[landscape[key].terrain] || settlements[key]) {
          return false;
        }
        reached.add(key);
        return true;
      });
  }
  return reached;
}

// NOTE: biomeAt(hex) gives the biome a hex's trees grow as. Given a start,
// the most wooded spots it can reach are planted until it has a few trees.
function surveyTrees(seed, landscape, settlements, world_x, world_y, biomeAt, world) {
  const result = [];
  const simplex = new SimplexNoise(seed);
  let generator = new MersenneTwister(seed);
  const spots = [];

  for (let sx = 0; sx < world_x / GRID_STEP; sx++) {
    const x = sx * GRID_STEP;
    for (let sy = 0; sy < world_y / GRID_STEP; sy++) {
      const y = sy * GRID_STEP;

      const hex = Hex().fromPoint(x, y);
      if (hex && landscape[hex] && landscape[hex].terrain == "grassland" && !settlements[hex]) {
        const point=world?.climate?worldPoint(world.home,{x,y}):null;
        const woods = point ? woodsAt(world,point) : Math.min(simplex.noise2D(x*.001,y*.001),.6)+simplex.noise2D(x*.01,y*.01)*.3;
        const habitat=biomeAt(hex);
        const probability=treeProbability(woods,habitat);
        const grows = probability>=1 || hashUnit(point ? world.climate.seed+":"+Math.round(point.x*1000)+","+Math.round(point.y*1000) : seed+":"+sx+","+sy)<probability;
        if (grows) {
          result.push({
            x: x + generator.random_int() % (GRID_STEP * 0.5) - GRID_STEP * 0.5,
            y: y + generator.random_int() % (GRID_STEP * 0.5) - GRID_STEP * 0.5
          });
        } else {
          spots.push({ x, y, hex, woods });
        }
      }
    }
  }

  return { trees: result, spots };
}

export function generateTrees(seed, landscape, settlements, world_x, world_y, { biomeAt, start, allowed = () => true, world, survey }) {
  const { trees, spots } = survey || surveyTrees(seed, landscape, settlements, world_x, world_y, biomeAt, world);
  let result = trees.filter(allowed);
  if (start) {
    result = result.filter(tree => start.distance(Hex().fromPoint(tree)) > 1);
    const reached = reachableFrom(start, landscape, settlements, START_TREE_REACH);
    const near = result.filter(t => reached.has(Hex().fromPoint(t).toString())).length;
    // NOTE: jittered like the rest, but kept in the spot's own hex, and
    // not on the start's own ring, which is kept clear for fields
    spots
      .map(spot => ({
        ...spot,
        tree: {
          x: spot.x - Math.floor(hashUnit(seed + ":x:" + spot.x + "," + spot.y) * GRID_STEP * 0.5) - 1,
          y: spot.y - Math.floor(hashUnit(seed + ":y:" + spot.x + "," + spot.y) * GRID_STEP * 0.5) - 1
        }
      }))
      .filter(spot => reached.has(spot.hex.toString()) && start.distance(spot.hex) >= 2 &&
        Hex().fromPoint(spot.tree).toString() == spot.hex.toString() && allowed(spot.tree))
      .sort((a, b) => b.woods - a.woods || a.x - b.x || a.y - b.y)
      .slice(0, Math.max(0, MIN_START_TREES - near))
      .forEach(spot => result.push(spot.tree));
  }
  return result;
}

const terrainValueFn = {
  "mountain": (distance) => distance <= 2 ? 2 : (15 - distance * 3),
  "shallow water": (distance) => (10 - distance * 2),
  "deep water": () => 0,
  "stone": (distance) => distance <= 2 ? 3 : (15 - distance * 3),
  "grassland": (distance) => (10 - distance * 2),
};

export const walkable = {
  "mountain": 0,
  "shallow water": 0,
  "deep water": 0,
  "stone": 0.5,
  "grassland": 1.0
};

async function generateEconomicValue(grid, landscape, progressUpdate) {
  const spiral = Grid.spiral({ radius: 5 });
  spiral.shift(); // Ignore centre
  for (let grid_index = 0; grid_index < grid.length; grid_index++) {
    let found = {};
    const target = grid[grid_index];
    if (landscape[target].terrain == "grassland") {
      for (let spiral_index = 0; spiral_index < spiral.length; spiral_index++) {
        const hex = target.add(spiral[spiral_index]);
        if (hex in landscape) {
          const terrain = landscape[hex].terrain;
          if (!(terrain in found)) {
            found[terrain] = terrainValueFn[terrain](target.distance(hex));
          }
        }
      }
      landscape[target].economic_value = Object.values(found).reduce((a, b) => a + b, 0);
    }
    if (grid_index % UPDATE_PROGRESS_EVERY == 0) {
      await progressUpdate("Finding fertile ground", .12 + .1 * (grid_index + 1) / grid.length);
    }
  }
}

function makeHouse(generator, settlements, grid, target, landscape) {
  Grid.spiral({ center: target, radius: 1}).forEach(hex => {
    landscape[hex].terrain = "grassland";
    if (settlements[hex]) {
      delete settlements[hex];
    }
  });
  settlements[target] = {
    x: target.x,
    y: target.y,
    frontDoorSide: generator.random_int() % 6,
  };
}

export async function generateSettlements(seed, grid, landscape, start, progressUpdate, mainLand, natural, contents) {
  const settlements = {};
  const villages = {};
  const generator = new MersenneTwister(seed);
  if (contents && !contents.settlements.length) {
    for(const h of Grid.hexagon({center:start,radius:1}))landscape[h].terrain="grassland";
    return {settlements,villages,settlementSites:[]};
  }
  // Keep clearings inland and leave the player's starting neighbourhood alone.
  const candidates = grid.filter(target => settlementSite(landscape, start, mainLand, natural, target));
  const ranked = candidates.map(hex => ({ hex, rank: generator.random() + landscape[hex].economic_value / 50 }))
    .sort((a, b) => b.rank - a.rank).map(c => c.hex);
  // The first hamlet is within visiting distance of the start, as before.
  const reached = reachableFrom(start, landscape, {}, 8);
  const near = ranked.filter(h => reached.has(h.toString())).sort((a, b) => start.distance(a) - start.distance(b));
  const centres = [];
  const targetCount = contents ? contents.settlements.length : Math.max(1, Math.round(grid.length / 140));
  const prefixes = ["Ash", "Bramble", "Cinder", "Elm", "Fern", "Hazel", "Oak", "Reed", "Thorn", "Willow", "Yew"];
  const suffixes = ["brook", "combe", "dale", "field", "ford", "ham", "hurst", "stead", "wick", "wood"];
  const nameOffset = generator.random_int() % prefixes.length;
  for (const centre of [...near, ...ranked]) {
    if (centres.length >= targetCount) break;
    if (centres.some(other => centre.distance(other) < 7)) continue;
    const sites = [centre, ...ranked.filter(h => h.toString() != centre.toString() && h.distance(centre) <= (contents?.kind === "town" ? 5 : 3))];
    const chosen = [];
    const size = contents?.settlements[centres.length]?.houseCount || 3 + generator.random_int() % 6;
    for (const site of sites) {
      // Extra pads impose grading constraints even though history has no
      // households for them. Survey only the homes this village needs.
      if (chosen.length >= size) break;
      if (chosen.every(h => h.distance(site) >= 2) &&
        Object.values(settlements).every(h => Hex(h).distance(site) >= 2)) chosen.push(site);
    }
    if (chosen.length < (contents ? size : 3)) continue;
    const id = contents?.settlements[centres.length]?.id || "village-" + (centres.length + 1);
    const name = prefixes[(nameOffset + centres.length) % prefixes.length] +
      suffixes[generator.random_int() % suffixes.length] + (centres.length >= prefixes.length ? " " + (centres.length + 1) : "");
    villages[id] = { id, name: contents?.settlements[centres.length]?.name || name, centre: { x: centre.x, y: centre.y }, houses: [] };
    centres.push(centre);
    for (const hex of chosen) {
      makeHouse(generator, settlements, grid, hex, landscape);
      settlements[hex].villageId = id;
      settlements[hex].villageCentre = hex.equals(centre);
      if(contents)settlements[hex].surveyRadius=5;
    }
    await progressUpdate("Founding villages", .22);
  }
  // Grading can reject a natural pad too. Keep the same inland survey and
  // footprint available to replace every recorded village's failed pads.
  const inland=contents ? grid.filter(h=>mainLand.has(String(h)) && start.distance(h)>3 && MAP_CENTRE.distance(h)<MAP_RADIUS-2 && !landscape[h].terrain.includes("water") && suitableHouse(natural,h.toPoint())) : [];
  const settlementSites=contents ? [...new Set([...ranked,...inland])] : ranked;
  if (contents && centres.length<targetCount) {
    // Stored households cannot disappear because a coast has fewer natural
    // pads. Survey inland foundations for every remaining recorded home.
    for(let vi=centres.length;vi<targetCount;vi++) {
      const specification=contents.settlements[vi];
      let chosen;
      for(const centre of [...ranked,...inland]) {
        if(centres.some(other=>centre.distance(other)<7))continue;
        const sites=[centre,...inland.filter(h=>h.distance(centre)<=5).sort((a,b)=>centre.distance(a)-centre.distance(b)||a.x-b.x||a.y-b.y)];
        const pads=[];
        for(const h of sites)if(pads.every(p=>p.distance(h)>=2) && Object.values(settlements).every(p=>Hex(p).distance(h)>=2))pads.push(h);
        if(pads.length>=specification.houseCount){chosen=pads.slice(0,specification.houseCount);break;}
      }
      if(!chosen)throw new Error("Cannot survey the stored households of "+specification.id);
      const centre=chosen[0],id=specification.id;
      villages[id]={id,name:specification.name,centre:{x:centre.x,y:centre.y},houses:[]};centres.push(centre);
      for(const h of chosen){makeHouse(generator,settlements,grid,h,landscape);settlements[h].villageId=id;settlements[h].villageCentre=h.equals(centre);settlements[h].surveyRadius=5;}
    }
  }
  // A coastal land hex can have no natural three-house clearing. It is still
  // a community destination: establish one inland village clearing, leaving
  // the continuous coastline alone. This is geography generation, not supplies
  // granted by travel, and is independent of the visitor or arrival side.
  if (!contents && targetCount && !centres.length && ranked.length) {
    const centre = ranked[0];
    const sites = [centre, ...ranked.filter(h => h.distance(centre) <= 3)];
    const chosen = [];
    for (const site of sites) if (chosen.length<3 && chosen.every(h=>h.distance(site)>=2)) chosen.push(site);
    const id = "village-1";
    villages[id] = { id, name: prefixes[nameOffset] + suffixes[generator.random_int()%suffixes.length], centre: {x:centre.x,y:centre.y}, houses: [] };
    for (const hex of chosen) { makeHouse(generator,settlements,grid,hex,landscape); settlements[hex].villageId=id; settlements[hex].villageCentre=hex.equals(centre); }
  }
  // Keep the bedroll clearing and its farm-sized ring free of trees.
  Grid.hexagon({ center: start, radius: 1 }).forEach(hex => {
    landscape[hex].terrain = "grassland";
  });
  return { settlements, villages, settlementSites };
}

function discoverStartingTiles(ecs, id, center) {
  const tiles = Grid.spiral({ center, radius: STARTING_KNOWN_DISTANCE })
    .map(s => ({ x: s.x, y: s.y }));
  discoverTiles(ecs, { id, tiles });
}

// The map lifecycle reset is independent of terrain size and history.
export function resetMapState(state) {
  state.space = {};
  state.redraws ||= [];
  state.snow = {};
  state.snowRevision = 0;
  state.environmentUpdatedAt = state.days;
}

export function removeMapPlayer(state) {
  for (const id of Object.keys(state.ecs.playable)) for (const component of Object.values(state.ecs)) if (component && typeof component == "object") delete component[id];
  state.ecs.playable = {};
}

export async function generateMap(state, seed, progressUpdate, options = {}) {
  await progressUpdate("Shaping the land", 0);
  // Entity ids restart at 1; an old index would point at different entities.
  resetMapState(state);
  state.world = options.world ? { ...options.world, home: { ...options.hex } } : generateWorld(seed);
  if (options.geographyFixture == "mountain-pass") {
    const centre = worldPoint(state.world.home, MAP_CENTRE.toPoint());
    state.world.geographyFixture = {kind:"mountain-pass", ...centre};
  }

  const grid = Grid.hexagon({ center: MAP_CENTRE, radius: MAP_RADIUS });
  let landscape = await generateTerrain(grid, state.world, progressUpdate);
  if (options.contents?.kind!=="empty") await generateEconomicValue(grid, landscape, progressUpdate);
  const fixtureStart = state.world.geographyFixture && grid.filter(h => MAP_CENTRE.distance(h)==MAP_RADIUS-2 &&
    Grid.hexagon({center:h,radius:2}).every(n=>landscape[n]?.terrain=="grassland"))
    .sort((a,b)=>a.toPoint().x-b.toPoint().x || a.y-b.y)[0];
  let start = Hex(fixtureStart || homeOf(state.world).start || options.start || { x: MAP_RADIUS, y: 2 });
  await progressUpdate("Founding villages", .22);
  const empty = options.contents?.kind === "empty";
  const { settlements, villages, settlementSites } = await generateSettlements(seed, grid, landscape, start, progressUpdate, empty ? undefined : mainLandKeys(state.world), empty ? undefined : naturalGroundFor(state.world), options.contents);
  // Village keys also cross maps in kinship, event and courtship records.
  if (state.ecs.namespace && !options.storedHistory) {
    for (const id of Object.keys(villages)) { const v = villages[id]; delete villages[id]; v.id = state.ecs.namespace + id; villages[v.id] = v; }
    for (const house of Object.values(settlements)) house.villageId = state.ecs.namespace + house.villageId;
  }
  state.villages = villages;
  const entrances = prepareEntrances(grid, landscape, settlements, state.world, MAP_CENTRE, MAP_RADIUS, options.surveyedEntrances);

  // Store physical habitat once per tile after the finished surface is known.
  const home = homeOf(state.world);
  const field = landFieldFor(state.world);
  await progressUpdate("Finding paths through the land", .3);
  const geography = buildGeography(grid, landscape, settlements, entrances, [start], {
    settlementSites, reserveHouseYards:true,
    houseCounts:options.contents && Object.fromEntries(options.contents.settlements.map(v=>[v.id,v.houseCount])),
    terrainAt:p=>field.terrainAt(worldPoint(state.world.home,p)),
    elevationAt:(x,y)=>field.elevationAt(worldPoint(state.world.home,{x,y}))
  });
  for (const id of Object.keys(villages)) if (!Object.values(settlements).some(h => h.villageId == id)) delete villages[id];
  const ground = groundForState({map:{seed,geography},ecs:{mappable:landscape,spatial:landscape},space:Object.fromEntries(Object.keys(landscape).map(k=>[k,[k]]))});
  for (const key in landscape) {
    const tile = landscape[key];
    if (walkable[tile.terrain] && !safeTile(ground,tile)) tile.terrain = "mountain";
    tile.elevation = ground.hex(tile).centre;
    const habitat=localHabitat(state.world,tile,tile.elevation);
    if(habitat)Object.assign(tile,habitat);
    else tile.temperature=tileTemperature(home.temperature,tile.terrain=="mountain" || tile.elevation>=HIGH_GROUND);
  }
  if (!options.world && !state.world.geographyFixture) {
    // Choose the initial camp on the finished surface. An optional road can be
    // absent, so the old survey coordinate is not proof of a walk to a village.
    const components = new Map(), houses = Object.values(settlements);
    const suitableArrival = hex => {
      if (!Grid.hexagon({ center: hex, radius: 2 }).every(h => landscape[h]?.terrain == "grassland") ||
        houses.some(h => hex.distance(Hex(h)) < MIN_START_SETTLEMENT_DISTANCE)) return false;
      let reached = components.get(String(hex));
      if (!reached) {
        reached = reachableFrom(hex, landscape, settlements, grid.length);
        for (const key of reached) components.set(key, reached);
      }
      return houses.some(h => hex.distance(Hex(h)) <= 8 && reached.has(String(
        Grid.hexagon({ center: Hex(h), radius: 1 }).neighborsOf(Hex(h), ALL_SIDES)[h.frontDoorSide])));
    };
    const alternatives = grid.filter(h => MAP_CENTRE.distance(h) == MAP_RADIUS - 2)
      .sort((a,b) => start.distance(a) - start.distance(b) || a.x-b.x || a.y-b.y);
    const chosen = [start, ...alternatives].find(suitableArrival);
    if (chosen) start = chosen;
  }
  await progressUpdate("Planting forests", .38);
  let trees = generateTrees(seed, landscape, {}, grid.pointWidth(), grid.pointHeight(), {
    world:state.world, biomeAt: hex => landscape[hex].biomeMix || biomeOf(landscape[hex].temperature,home.moisture),
    start
  }).filter(tree => {
    const hex = Hex().fromPoint(tree);
    return landscape[hex]?.terrain == "grassland";
  });
  // Every pass has the same initial resource guarantees as the opening.
  // All passes use the same natural forest roll. Only their local clearing and
  // minimum-tree guarantee differ; do not reroll the entire forest per pass.
  const passTreeSurvey = Object.keys(entrances).length ? surveyTrees(seed, landscape, settlements, grid.pointWidth(), grid.pointHeight(),
    hex => landscape[hex].biomeMix || biomeOf(landscape[hex].temperature,home.moisture), state.world) : undefined;
  for (const entry of Object.values(entrances)) {
    const extra = generateTrees(seed, landscape, settlements, grid.pointWidth(), grid.pointHeight(), {
      world:state.world, survey:passTreeSurvey, biomeAt: hex => landscape[hex].biomeMix || biomeOf(landscape[hex].temperature,home.moisture), start: Hex(entry.hexes[2])
    });
    const reached = reachableFrom(Hex(entry.hexes[2]), landscape, settlements, 4);
    for (const tree of extra.filter(t => reached.has(String(Hex().fromPoint(t))) && Hex(entry.hexes[2]).distance(Hex().fromPoint(t)) >= 2)) {
      if (!trees.some(t => Math.hypot(t.x-tree.x,t.y-tree.y)<10)) trees.push(tree);
    }
  }
  const generator = new MersenneTwister(seed);

  const tiles = Object.values(landscape).map((tile) => ({
    nameable: { nickname: "Map tile" },
    spatial: { x: tile.x, y: tile.y, immovable: true },
    mappable: { terrain: tile.terrain, temperature: tile.temperature, climateTemperature: home.temperature, climateWarmth:tile.climateWarmth, moistureValue:tile.moistureValue, warmth:tile.warmth, biomeMix:tile.biomeMix, biome:tile.biome, elevation: tile.elevation, ...(tile.route ? {route:tile.route} : {}) },
    traits: { values: {} },
    valuable: { value: tile.economic_value },
    walkable: { worn: {}, speed: walkable[tile.terrain], neighbours: {} },
  }));

  const treeEntities = trees.map(tree => ({
    nameable: { nickname: "Tree" },
    spatial: { x: tree.x, y: tree.y, immovable: true },
    good: {
      type: "wood",
      amount: (generator.random_int() % 3) + 1
    }
  }));

  newEntities(state, [ ...tiles, ...treeEntities ]);
  state.ecs.building ||= {};
  state.ecs.container ||= {};
  state.ecs.household ||= {};
  for (const walk of Object.values(state.ecs.walkable)) { walk.routeWear = 0; walk.wearUpdatedAt = -543; }

  for (const id in state.ecs.walkable) {
    if (state.ecs.walkable[id].speed > 0) {
      const hex = Hex().fromPoint(state.ecs.spatial[id]);
      const grid = Grid.hexagon({ center: hex, radius: 1 });
      grid.neighborsOf(hex, ALL_SIDES).forEach((nHex, side) => {
        if (!(nHex in state.space)) {
          return;
        }
        const mId = state.space[nHex].filter(e => state.ecs.mappable[e])[0];
        if (mId && state.ecs.walkable[mId].speed > 0) {
          addBidirectionalLink(state.ecs, id, side, mId);
        }
      });
    }
  }

  const playerStartTile = { x: start.x, y: start.y };
  const map = {
    seed,
    playerStartTile,
    centre: { x: MAP_CENTRE.x, y: MAP_CENTRE.y },
    radius: MAP_RADIUS, entrances, geography, worldKey: worldKey(state.world.home)
  };

  state.map = map;
  for (const entry of Object.values(entrances)) entry.tileIds = entry.hexes.map(h => tileAt(state, Hex(h).toPoint()));
  if (options.storedHistory) {
    await progressUpdate("Building the remembered households", .65);
    installStoredHistory(state, options.storedHistory, settlements, options.contents, options.noPlayer);
    if (Object.keys(state.ecs.playable).length) discoverStartingTiles(state.ecs, Object.keys(state.ecs.playable)[0], playerStartTile);
    for (const house of Object.keys(state.ecs.building)) reserveBuildingClearance(state,house);
    generateVillageLife(state, seed, playerStartTile);
    state.settlement={version:2,lastSeason:state.social.lastSeason,preparedSeason:state.social.lastSeason,sites:{},journeys:[],appliedJourneys:{},observedAt:state.days};
    initializeEcology(state, seed);
    generateForage(state, seed, playerStartTile);
    for (const entry of Object.values(entrances)) {
      generateForage(state, seed, entry.hexes[2], { onlyStart: true });
      entry.villageReachable = !!Object.keys(state.ecs.building).length;
    }
    if (options.contents.roads) {
      markThroughRoutes(state, Object.values(state.villages).filter(v=>v.houses.length).map(v=>{
        const id=v.houses[0],tile=tileAt(state,state.ecs.spatial[id]);
        return state.ecs.walkable[tile].neighbours[state.ecs.building[id].entrance];
      }).filter(Boolean));
      for (const [id,tile] of Object.entries(state.ecs.mappable)) if (tile.route?.length) {
        state.ecs.walkable[id].routeWear=FULL_TRACK;state.ecs.walkable[id].wearUpdatedAt=state.days;
      }
    }
    updateWeather(state, 0, options.worldSeed ?? seed);
    await progressUpdate("Ready to arrive", 1);
    return { playerId: Object.keys(state.ecs.playable)[0], map: state.map };
  }
  state.settlement = { version: 1, lastSeason: (-HISTORY_SEASONS - 1), preparedSeason: (-HISTORY_SEASONS - 1), sites: {}, journeys: [], appliedJourneys: {}, sealedBorders: options.sealedBorders || [] };
  const transitRoutes=geography.routes.filter(route=>Object.values(entrances).some(e=>Hex(e.hexes[2]).equals(Hex(route[0]))));
  const transitHexes=transitRoutes.flat().map(h=>Hex(h));
  for(const hex of transitHexes)state.ecs.mappable[tileAt(state,hex.toPoint())].settlementTransit=true;
  for (const [index,village] of Object.values(villages).entries()) {
    const foundingSeason = historyFoundingSeason(hashUnit(seed+":founding:"+village.id), !index);
    const sites=Object.values(settlements).filter(h=>h.villageId==village.id);
    const approach=site=>Grid.hexagon({center:Hex(site),radius:1}).neighborsOf(Hex(site),ALL_SIDES)[site.frontDoorSide];
    const access=site=>Math.min(...transitHexes.map(h=>h.distance(approach(site))));
    sites.sort((a,b)=>access(a)-access(b) || Number(b.villageCentre)-Number(a.villageCentre));
    const first=sites.shift();
    sites.sort((a,b)=>Hex(first).distance(Hex(a))-Hex(first).distance(Hex(b)) || a.x-b.x || a.y-b.y);
    const ordered=[first,...sites];
    state.settlement.sites[village.id] = ordered.map((h,i)=>({point:Hex(h.x,h.y).toPoint(),entrance:h.frontDoorSide,...(h.yardHex?{yardPoint:Hex(h.yardHex).toPoint()}:{}),establishSeason:foundingSeason+i*2}));
    village.access={kind:transitHexes.length?"regional route":"local route",crossings:Object.values(entrances).map(e=>e.key),distanceToTransit:transitHexes.length?access(first):null};
    village.sharedPlaces = [];
  }
  // Prospective pads reserve one approach. History cannot route a through
  // connection across a house site and later construct across that road.
  for(const site of Object.values(state.settlement.sites).flat())site.navigationLinks={...state.ecs.walkable[tileAt(state,site.point)].neighbours};
  for(const site of Object.values(state.settlement.sites).flat()) {
    const tile=tileAt(state,site.point);
    for(const side of ALL_SIDES)if(side!=site.entrance)removeBidirectionalLink(state.ecs,tile,side);
  }
  reserveFoundingFields(state);
  const prosperityVillage=options.settlementFixture=="prosperity" && Object.values(villages).find(v=>state.settlement.sites[v.id].length>=5);
  if(prosperityVillage)for(const site of state.settlement.sites[prosperityVillage.id])site.establishSeason=-1;
  await progressUpdate("Settling the first families", .45);
  generateFamilies({ state, seed, playerStartTile, founding: true });
  initializeEcology(state,seed);
  await generateHistory(state, seed, options.routeHistory || [], completed =>
    progressUpdate(historyLabel(completed), .45 + .45 * completed / HISTORY_SEASONS));
  await progressUpdate("Waking the villagers", .9);
  const playerId = Object.values(state.ecs.playable)[0].id;
  state.ecs.controllable[playerId].controllerId = playerId;
  discoverStartingTiles(state.ecs, playerId, playerStartTile);
  generateVillageLife(state, seed, playerStartTile);
  markThroughRoutes(state,Object.values(state.villages).map(village=>{
    const house=village.houses[0], tile=tileAt(state,state.ecs.spatial[house]);
    return state.ecs.walkable[tile].neighbours[state.ecs.building[house].entrance];
  }));
  if(prosperityVillage) {
    const v=prosperityVillage;
    const eligible=v.houses.filter(id=>state.ecs.household[id].trade && expansionFits(state,id,expandedFootprint(state,id,1)) && expansionFits(state,state.ecs.household[id].workplaceId,expandedFootprint(state,state.ecs.household[id].workplaceId,1)));
    const prosperous=eligible.find(id=>["weaver","tanner"].includes(state.ecs.household[id].trade)) || eligible[0] || v.houses.find(id=>state.ecs.household[id].trade);
    const pub=v.sharedPlaces.find(id=>state.ecs.meetingPlace[id].kind=="alehouse");
    const host=pub && state.ecs.meetingPlace[pub].houseId;
    const poor=v.houses.find(id=>id!=prosperous && id!=host);
    const supply=(house,wood,grain)=>{
      const h=state.ecs.household[house],store=state.ecs.container[h.storeId];
      for(const [type,target] of [["wood",wood],["grain",grain]]) {
        const delta=target-(store.amounts[type] || 0);
        store.amounts[type]=target;
        h.estateAccount.initial[type]=(h.estateAccount.initial[type] || 0)+delta;
      }
    };
    supply(prosperous,80,60);
    if(host && host!=prosperous)supply(host,40,60);
    supply(poor,0,24);
    state.ecs.container[playerId]={id:playerId,capacity:96,amounts:{gruel:48,grain:24}};
    state.settlement.fixture={observerSupplies:"48 carried gruel and 24 carried grain",kind:"prosperity",label:"Initial prosperity setup",villageId:v.id,prosperousHouse:prosperous,workplaceId:state.ecs.household[prosperous].workplaceId,pubId:pub,pubHouse:host,poorHouse:poor,initialDay:state.days,stocks:"Trade household: 80 timber and 60 grain. Pub household: 40 timber and 60 grain if separate. Comparison household: no timber, 24 grain."};
  }
  await progressUpdate("Preparing the village stores", .95);
  giveTradeTools(state);
  // The existing starting-wood guarantee is placed outside actual roofs and
  // approaches after household geometry is known; clearance never yields logs.
  for (const clearing of [start, ...Object.values(entrances).map(e=>Hex(e.hexes[2]))]) {
    const reached = reachableFrom(clearing, landscape, settlements, 4);
    const safe = point => treeAllowed(state,point) && !(state.space[Hex().fromPoint(point)] || []).some(id=>
      state.ecs.mappable[id]?.villagePlot || state.ecs.farmable?.[id] || state.ecs.stockpile?.[id]);
    const extra = generateTrees(seed, landscape, settlements, grid.pointWidth(), grid.pointHeight(), {
      world:state.world, survey:passTreeSurvey, biomeAt: hex=>landscape[hex].biomeMix || biomeOf(landscape[hex].temperature,home.moisture), start:clearing, allowed:safe
    });
    for(const tree of extra.filter(p=>reached.has(String(Hex().fromPoint(p))))) {
      if(Object.keys(state.ecs.good).some(id=>state.ecs.good[id].type=="wood" && Math.hypot(state.ecs.spatial[id].x-tree.x,state.ecs.spatial[id].y-tree.y)<10)) continue;
      newEntities(state,[{spatial:{...tree,immovable:true},nameable:{nickname:"Tree"},good:{type:"wood",amount:1+Math.floor(hashUnit(seed+":wood:"+tree.x+","+tree.y)*3)}}]);
    }
  }
  const naturalCaps=state.ecology.treeCaps;
  initializeEcology(state, seed);
  for(const [biome,count] of Object.entries(naturalCaps))state.ecology.treeCaps[biome]=Math.max(count,state.ecology.treeCaps[biome] || 0);
  generateForage(state, seed, playerStartTile);
  for (const entry of Object.values(entrances)) {
    entry.tileIds = entry.hexes.map(h => tileAt(state, Hex(h).toPoint()));
    // generateForage is idempotent only for guaranteed nearby spots; avoid
    // rerolling the entire ecology for each pass.
    generateForage(state, seed, entry.hexes[2], { onlyStart: true });
    const reached = new Set(), queue = [[entry.tileIds[2], 0]];
    for (const [id, depth] of queue) if (!reached.has(id)) { reached.add(id); if (depth < 4) for (const next of Object.values(state.ecs.walkable[id].neighbours)) queue.push([next, depth+1]); }
    const near = id => reached.has(tileAt(state, state.ecs.spatial[id]));
    const connected = new Set(), frontier = [entry.tileIds[2]];
    for(let i=0;i<frontier.length;i++) {
      const id=frontier[i];if(connected.has(id))continue;connected.add(id);
      for(const next of Object.values(state.ecs.walkable[id].neighbours)) if(!connected.has(next))frontier.push(next);
    }
    entry.villageReachable = Object.keys(state.ecs.building).some(id=>connected.has(tileAt(state,state.ecs.spatial[id])));
    entry.wood = Object.keys(state.ecs.tree || {}).filter(near).length;
    entry.food = Object.keys(state.ecs.forageable || {}).filter(near).length;
  }
  if (options.noPlayer) removeMapPlayer(state);
  updateWeather(state, 0, options.worldSeed ?? seed);
  await progressUpdate("Ready to arrive", 1);

  // History can apply real house grading to a newer map object. Return that
  // authoritative surface, so callers cannot restore the prehistory survey.
  return { playerId, map: state.map };
}

function mid(a, b) {
  return math.lerp(a, b, 0.5);
}

const generateTriangle = () => {
  const ctr = Hex().center();
  const c = Hex().corners();
  return [
    c[4], mid(c[4], c[5]), c[5],
    mid(c[3], c[4]), mid(ctr, c[4]), mid(ctr, c[5]), mid(c[5], c[0]),
    c[3], mid(ctr, c[3]), ctr, mid(ctr, c[0]), c[0],
    mid(c[2], c[3]), mid(ctr, c[2]), mid(ctr, c[1]), mid(c[1], c[0]),
    c[2], mid(c[1], c[2]), c[1]
  ].map(v => math.lerp(v, ctr, 0.25));
};

export const TRIANGLES = generateTriangle();
export const TRIANGLE_INTERIOR_RADIUS = HEX_SIZE / 6;

export function triangleCenters({x, y}) {
  return TRIANGLES.map(p => ({ x: p.x + x, y: p.y + y }));
}
